import * as classnames from 'classnames'
import get from 'lodash-es/get'
import * as React from 'react'

import { reportImageCarouselClick } from '../../../common/analytics/imageCarouselAnalytics'
import ui from '../../../common/constants/ui'
import { IItem } from '../../../typings/searchResults'
import ImagePlaceholder from '../../components/imagePlaceholder'
import EnhancedLazyLoad from '../enhancedLazyLoad'
import ArrowLeftIcon from '../icons/carouselArrowLeft'
import ArrowRightIcon from '../icons/carouselArrowRight'

import * as styles from './imagePreviewWithCarousel.module.css'

interface IProps {
  item: IItem
  onItemLinkClick: React.EventHandler<any>
}

interface IState {
  currentImgIndex: number
}

interface IImageDef {
  url: string
  loaded: boolean
}

class ImagePreviewWithCarousel extends React.Component<IProps, IState> {
  private images: IImageDef[]

  constructor(props: IProps) {
    super(props)

    this.images = this.getImages(props.item)

    this.state = {
      currentImgIndex: 0
    }
  }

  public render() {
    const { item, onItemLinkClick } = this.props

    return (
      <div className={styles.carousel}>
        <a
          href={`${item.url}`}
          onClick={onItemLinkClick}
          aria-label={item.name}
        >
          <ImagePlaceholder ratio={300 / 590}>
            <EnhancedLazyLoad height="100%" once offset={ui.lazyLoadOffset}>
              <div>
                {this.images.map((img, i) => this.carouselImageForIndex(i))}
              </div>
            </EnhancedLazyLoad>
          </ImagePlaceholder>
        </a>
        {this.images.length > 1 &&
          this.carouselControls(this.selectImage(item.id), this.images.length)}
      </div>
    )
  }

  private carouselControls = (
    selectImage: (step: number, limit: number) => void,
    max: number
  ): any => {
    const arrowLeft = classnames(styles.arrow, styles.arrowLeft)
    const arrowRight = classnames(styles.arrow, styles.arrowRight)

    return (
      <React.Fragment>
        <ArrowLeftIcon
          className={arrowLeft}
          onClick={() => selectImage(-1, 0)}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.keyCode === 13) {
              selectImage(-1, 0)
            }
          }}
          tabIndex="0"
        />
        <ArrowRightIcon
          className={arrowRight}
          onClick={() => selectImage(1, max - 1)}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.keyCode === 13) {
              selectImage(1, max - 1)
            }
          }}
          tabIndex="0"
        />
      </React.Fragment>
    )
  }

  private selectImage = (itemID: number) => (step: number, limit: number) => {
    reportImageCarouselClick(itemID)

    this.state.currentImgIndex === limit
      ? this.setState({ currentImgIndex: this.images.length - 1 - limit })
      : this.setState({ currentImgIndex: this.state.currentImgIndex + step })
  }

  private carouselImageForIndex = (imgIndex: number): any => {
    const {
      item: { name }
    } = this.props

    return (
      <img
        style={{ left: `${100 * (imgIndex - this.state.currentImgIndex)}%` }}
        className={styles.carouselImage}
        src={this.srcUrlForImageAtIndex(imgIndex)}
        title={name}
        alt={name}
        key={imgIndex}
      />
    )
  }

  private srcUrlForImageAtIndex = (imageIndex: number): string => {
    // 2nd layer of lazy load:
    // once a carousel is in viewport, preload only next image
    const isFirstOrSecondImage = imageIndex <= 1
    const isLastImage = imageIndex === this.images.length - 1
    const imageHasAlreadyBeenLoaded = this.images[imageIndex].loaded
    const hasCarouselBeenInteractedWith = this.state.currentImgIndex >= 1
    const shouldPreloadLoadDueToInteraction =
      hasCarouselBeenInteractedWith &&
      imageIndex <= this.state.currentImgIndex + 1

    const shouldSetImageSrc =
      isFirstOrSecondImage ||
      isLastImage ||
      imageHasAlreadyBeenLoaded ||
      shouldPreloadLoadDueToInteraction

    if (shouldSetImageSrc) {
      this.images[imageIndex].loaded = true
      return this.images[imageIndex].url
    }
    return ''
  }

  private getImages = (item: IItem): IImageDef[] => {
    let previewImages = this.getLandscapePreviewImages(item)

    if (previewImages.length === 0) {
      previewImages = this.getFallbackLandscapePreviewImages(item)
    }

    if (previewImages.length === 0) {
      previewImages = this.getVideoPreviewImages(item)
    }

    if (previewImages.length === 0) {
      previewImages = this.blankPreviewImagesIfDataIsMissing()
    }

    return previewImages
  }

  private blankPreviewImagesIfDataIsMissing = () => {
    return [this.unloadedImageForUrl('')]
  }

  private getLandscapePreviewImages = (item: IItem): IImageDef[] => {
    const previewImages = get(item, 'previews.landscape_preview.image_urls')
    return previewImages && previewImages.length
      ? previewImages.map((i: any) => this.unloadedImageForUrl(get(i, 'url')))
      : []
  }

  private unloadedImageForUrl = (url: string): IImageDef => ({
    url,
    loaded: false
  })

  private getFallbackLandscapePreviewImages = (item: IItem): IImageDef[] => {
    const url = get(item, 'previews.landscape_preview.landscape_url')
    return url ? [this.unloadedImageForUrl(url)] : []
  }

  private getVideoPreviewImages = (item: IItem): IImageDef[] => {
    const url = get(item, 'previews.icon_with_video_preview.landscape_url')
    return url ? [this.unloadedImageForUrl(url)] : []
  }
}

export default ImagePreviewWithCarousel
